import React, { FC } from 'react';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import { GetRewardsConfigResponse } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { maxRafflePrizeForPhase } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { BonusEntriesBadge } from 'Rewards/components/BonusEntriesBadge';
import styles from './styles.module.css';

export type RaffleEntryCardFullProps = {
  status: GetAgentStatusResponse | null;
  config: GetRewardsConfigResponse | null;
  /** Whether the raffle submission is in progress (disables the button while pending). */
  submitPending: boolean;
  /** Called when the producer clicks "ENTER RAFFLE". Not called when the button is disabled. */
  onEnterRaffle: () => void;
};

export const RaffleEntryCardFull: FC<RaffleEntryCardFullProps> = ({
  status,
  config,
  submitPending,
  onEnterRaffle,
}) => {
  const currentPhaseCode = status?.phase?.code ?? config?.phase?.code ?? null;
  const maxPrize =
    config && currentPhaseCode
      ? maxRafflePrizeForPhase(config, currentPhaseCode)
      : null;

  const regularEntries = status?.balance?.regular ?? 0;
  const bonusEntries = status?.balance?.bonus ?? 0;
  const totalEntries = regularEntries + bonusEntries;
  const canEnter = totalEntries > 0 && !submitPending;

  const policiesRemaining = status?.progress?.policiesUntilNextEntry ?? 0;
  const entryThreshold = config?.config?.program?.entry_threshold ?? null;
  const filledSegments =
    entryThreshold != null
      ? Math.max(0, entryThreshold - policiesRemaining)
      : 0;

  return (
    <div className={styles.raffleCard} data-testid="raffle-entry-card-full">
      {/* Label + headline */}
      <p className={styles.cardLabel}>RAFFLE ENTRY</p>
      {maxPrize != null && (
        <p
          className={styles.prizeHeadline}
          data-testid="raffle-entry-full-headline"
        >
          Win up to ${maxPrize}
        </p>
      )}

      {/* Entry counts */}
      <div className={styles.entriesRow} data-testid="raffle-entry-full-counts">
        <span
          className={styles.entriesCount}
          data-testid="raffle-entry-full-regular-count"
        >
          {regularEntries} ENTRIES EARNED
        </span>
        <BonusEntriesBadge
          phaseNumber={status?.phase?.number ?? 1}
          bonusCount={bonusEntries}
        />
      </div>

      {/* Progress toward next entry */}
      {status?.progress && entryThreshold != null && (
        <div
          className={styles.progressSection}
          data-testid="raffle-entry-full-progress"
        >
          <div className={styles.progressLabels}>
            <span>NEXT ENTRY</span>
            <span>
              {policiesRemaining} {policiesRemaining === 1 ? 'SALE' : 'SALES'}{' '}
              AWAY
            </span>
          </div>
          <div
            className={styles.progressBar}
            data-testid="raffle-entry-full-progress-bar"
          >
            {Array.from({ length: entryThreshold }).map((_, i) => (
              <div
                key={i}
                className={
                  i < filledSegments
                    ? styles.progressSegmentFilled
                    : styles.progressSegmentEmpty
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* ENTER RAFFLE button — disabled when no entries (AC4) */}
      <button
        className={styles.enterRaffleButton}
        onClick={onEnterRaffle}
        disabled={!canEnter}
        data-testid="raffle-entry-enter-button"
        aria-disabled={!canEnter}
      >
        {submitPending ? 'SUBMITTING…' : 'ENTER RAFFLE'}
      </button>
    </div>
  );
};
